import React, {forwardRef, useRef} from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {SxProp} from '@fuegokit/react/sx'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import {
  DropdownArrowProps,
  DropdownCheckboxItemProps,
  DropdownContentProps,
  DropdownGroupProps,
  DropdownItemIndicatorProps,
  DropdownItemProps,
  DropdownLabelProps,
  DropdownPortalProps,
  DropdownRadioGroupProps,
  DropdownRadioItemProps,
  DropdownProps,
  DropdownSeparatorProps,
  DropdownSubContentProps,
  DropdownSubProps,
  DropdownSubTriggerProps,
  DropdownTriggerProps
} from './types'

// --------------------------------------------------------
// Dropdown Root
/**
 *
 * Contains all the parts of a dropdown menu.
 */
const Root = ({children, ...props}: DropdownProps) => {
  return <DropdownMenu.Root {...props}>{children}</DropdownMenu.Root>
}
// --------------------------------------------------------
// Dropdown Trigger
/**
 *
 * The button that toggles the dropdown menu. By default, the DropdownMenu.Content will position itself against the trigger.
 *
 */
const Trigger = forwardRef(({children, ...props}: DropdownTriggerProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <DropdownMenu.Trigger ref={innerRef} {...props}>
      {children}
    </DropdownMenu.Trigger>
  )
})
// --------------------------------------------------------
// Dropdown Portal
/**
 *
 * When used, portals the content part into the body.
 *
 */
const Portal = ({children, container = document.body, ...props}: DropdownPortalProps) => {
  return (
    <DropdownMenu.Portal container={container} {...props}>
      {children}
    </DropdownMenu.Portal>
  )
}

// --------------------------------------------------------
// Dropdown Content
/**
 *
 * The component that pops out when the dropdown menu is open.
 *
 */
const Content = ({children, ...props}: DropdownContentProps) => {
  const {
    asChild,
    loop,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    side = 'bottom',
    sideOffset = 0,
    align = 'center',
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary,
    collisionPadding = 0,
    arrowPadding = 0,
    sticky = 'partial',
    hideWhenDetached = false,
    UNSAFE_className
  } = props
  return (
    <DropdownMenu.Content
      asChild={asChild}
      loop={loop}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={onEscapeKeyDown}
      onPointerDownOutside={onPointerDownOutside}
      onFocusOutside={onFocusOutside}
      onInteractOutside={onInteractOutside}
      side={side}
      sideOffset={sideOffset}
      align={align}
      alignOffset={alignOffset}
      avoidCollisions={avoidCollisions}
      collisionBoundary={collisionBoundary}
      collisionPadding={collisionPadding}
      arrowPadding={arrowPadding}
      sticky={sticky}
      hideWhenDetached
      className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.Content>
  )
}
// --------------------------------------------------------
// Dropdown Arrow
/**
 *
 * An optional arrow element to render alongside the dropdown menu. This can be used to help visually link the trigger with the Dropdown.Content. Must be rendered inside Dropdown.Content.
 *
 */
const Arrow = ({children, asChild = false, UNSAFE_className, width = 10, height = 5, ...props}: DropdownArrowProps) => {
  return (
    <DropdownMenu.Arrow asChild={asChild} className={UNSAFE_className} {...props}>
      {children}
    </DropdownMenu.Arrow>
  )
}
// --------------------------------------------------------
// Dropdown Item
/**
 *
 * The component that contains the dropdown menu items.
 *
 */
const Item = ({children, ...props}: DropdownItemProps & SxProp) => {
  const {asChild = false, disabled, onSelect, textValue, sx, UNSAFE_className} = props
  return (
    <DropdownMenu.Item
      asChild={asChild}
      disabled={disabled}
      onSelect={onSelect}
      textValue={textValue}
      sx={sx}
      UNSAFE_className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.Item>
  )
}
// --------------------------------------------------------
// Dropdown Group
/**
 *
 * Used to group multiple DropdownMenu.Items
 *
 */
const Group = ({children, asChild = false, ...props}: DropdownGroupProps) => {
  return (
    <DropdownMenu.Group asChild={asChild} {...props}>
      {children}
    </DropdownMenu.Group>
  )
}
// --------------------------------------------------------
// Dropdown Label
/**
 *
 * Used to render a label. It won't be focusable using arrow keys.
 *
 */
const Label = ({children, asChild = false, UNSAFE_className, ...props}: DropdownLabelProps) => {
  return (
    <DropdownMenu.Label asChild={asChild} className={UNSAFE_className} {...props}>
      {children}
    </DropdownMenu.Label>
  )
}
// --------------------------------------------------------
// Dropdown CheckboxItem
/**
 *
 * An item that can be controlled and rendered like a checkbox.
 *
 */
const CheckboxItem = ({
  children,
  asChild = false,
  checked,
  onCheckedChange,
  disabled,
  onSelect,
  textValue,
  UNSAFE_className,
  ...props
}: DropdownCheckboxItemProps) => {
  return (
    <DropdownMenu.CheckboxItem
      asChild={asChild}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      onSelect={onSelect}
      textValue={textValue}
      className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.CheckboxItem>
  )
}
// --------------------------------------------------------
// Dropdown RadioGroup
/**
 *
 * Used to group multiple DropdownMenu.RadioItems.
 *
 */
const RadioGroup = ({children, asChild = false, ...props}: DropdownRadioGroupProps) => {
  return (
    <DropdownMenu.RadioGroup asChild={asChild} {...props}>
      {children}
    </DropdownMenu.RadioGroup>
  )
}
// --------------------------------------------------------
// Dropdown RadioItem
/**
 *
 * An item that can be controlled and rendered like a radio.
 *
 */
const RadioItem = ({
  children,
  value,
  onSelect,
  textValue,
  asChild = false,
  UNSAFE_className,
  ...props
}: DropdownRadioItemProps) => {
  return (
    <DropdownMenu.RadioItem
      value={value}
      onSelect={onSelect}
      textValue={textValue}
      asChild={asChild}
      className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.RadioItem>
  )
}
// --------------------------------------------------------
// Dropdown Sub
/**
 *
 * Contains all the parts of a submenu.
 *
 */
const Sub = ({children, defaultOpen, open, onOpenChange, ...props}: DropdownSubProps) => {
  return (
    <DropdownMenu.Sub defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange} {...props}>
      {children}
    </DropdownMenu.Sub>
  )
}
// --------------------------------------------------------
// Dropdown SubTrigger
/**
 *
 * An item that opens a submenu. Must be rendered inside Dropdown.Sub
 *
 */
const SubTrigger = ({children, asChild, disabled, textValue, UNSAFE_className, ...props}: DropdownSubTriggerProps) => {
  return (
    <DropdownMenu.SubTrigger
      asChild={asChild}
      disabled={disabled}
      textValue={textValue}
      className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.SubTrigger>
  )
}
// --------------------------------------------------------
// Dropdown SubContent
/**
 *
 * The component that pops out when a submenu is open. Must be rendered inside Dropdown.Sub.
 *
 */
const SubContent = ({children, ...props}: DropdownSubContentProps) => {
  const {
    asChild = false,
    loop = false,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    sideOffset = 0,
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary,
    collisionPadding = 0,
    arrowPadding,
    sticky,
    hideWhenDetached,
    UNSAFE_className
  } = props
  return (
    <DropdownMenu.SubContent
      asChild={asChild}
      loop={loop}
      onEscapeKeyDown={onEscapeKeyDown}
      onPointerDownOutside={onPointerDownOutside}
      onFocusOutside={onFocusOutside}
      onInteractOutside={onInteractOutside}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      avoidCollisions={avoidCollisions}
      collisionBoundary={collisionBoundary}
      collisionPadding={collisionPadding}
      arrowPadding={arrowPadding}
      sticky={sticky}
      hideWhenDetached={hideWhenDetached}
      className={UNSAFE_className}
      {...props}
    >
      {children}
    </DropdownMenu.SubContent>
  )
}
// --------------------------------------------------------
// Dropdown Separator
/**
 *
 * Used to visually separate items in the dropdown menu.
 *
 */
const Separator = ({children, asChild, UNSAFE_className, ...props}: DropdownSeparatorProps) => {
  return (
    <DropdownMenu.Separator asChild={asChild} className={UNSAFE_className} {...props}>
      {children}
    </DropdownMenu.Separator>
  )
}
// --------------------------------------------------------
// Dropdown ItemIndicator
/**
 *
 * Renders when the parent DropdownMenu.CheckboxItem or DropdownMenu.RadioItem is checked. You can style this element directly, or you can use it as a wrapper to put an icon into, or both.
 *
 */
const ItemIndicator = ({children, asChild, UNSAFE_className, ...props}: DropdownItemIndicatorProps) => {
  return (
    <DropdownMenu.ItemIndicator asChild={asChild} className={UNSAFE_className} {...props}>
      {children}
    </DropdownMenu.ItemIndicator>
  )
}
// --------------------------------------------------------
// Exports
Root.displayName = 'Dropdown'
Trigger.displayName = 'Trigger'
Content.displayName = 'Content'
Item.displayName = 'Item'
Portal.displayName = 'Portal'
Content.displayName = 'Content'
Group.displayName = 'Group'
Sub.displayName = 'Sub'
SubTrigger.displayName = 'SubTrigger'
SubContent.displayName = 'SubContent'
Separator.displayName = 'Separator'
CheckboxItem.displayName = 'CheckboxItem'
ItemIndicator.displayName = 'ItemIndicator'
Label.displayName = 'Label'
RadioGroup.displayName = 'RadioGroup'
Arrow.displayName = 'Arrow'
RadioItem.displayName = 'RadioItem'
/**
 * Displays a menu to the user—such as a set of actions or functions—triggered by a button.
 */
export default Object.assign(Root, {
  Trigger,
  Item,
  Content,
  Group,
  Sub,
  SubTrigger,
  Portal,
  SubContent,
  Separator,
  CheckboxItem,
  ItemIndicator,
  Label,
  RadioGroup,
  Arrow,
  RadioItem
})
